import { type ReactNode } from 'react';
import { AuthProvider as OidcAuthProvider, useAuth } from 'react-oidc-context';
import { WebStorageStateStore } from 'oidc-client-ts';
import { setAuthTokenGetter } from '@/lib/api';

const oidcConfig = {
  authority: import.meta.env.VITE_COGNITO_AUTHORITY,
  client_id: import.meta.env.VITE_COGNITO_CLIENT_ID,
  redirect_uri: import.meta.env.VITE_REDIRECT_URI,
  post_logout_redirect_uri: import.meta.env.VITE_LOGOUT_URI,
  response_type: 'code',
  scope: 'openid email profile',
  // Strip ?code= and ?state= from URL after the code exchange happens.
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, window.location.pathname);
  },
  // localStorage so the user stays signed in across tabs and restarts.
  // Token lifetimes (1h access, 30d refresh) and automaticSilentRenew handle
  // rotation. XSS risk is the same as sessionStorage — anything running on
  // the origin can read either.
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  automaticSilentRenew: true,
};

function ApiTokenWiring({ children }: { children: ReactNode }) {
  const auth = useAuth();
  // Update during render, not in useEffect: child queryFns can fire in
  // their own useEffect on the same render, before this provider's
  // post-render effect would have run, leading to a missing Authorization
  // header on the first authed request after sign-in.
  setAuthTokenGetter(() => auth.user?.id_token ?? null);
  return <>{children}</>;
}

export function AuthProviderRoot({ children }: { children: ReactNode }) {
  return (
    <OidcAuthProvider {...oidcConfig}>
      <ApiTokenWiring>{children}</ApiTokenWiring>
    </OidcAuthProvider>
  );
}
